export interface CountryData {
  code: string;
  name: string;
  flag: string;
  demonym: string;
  currency: {
    code: string;
    symbol: string;
    exchangeRate: number; // to EUR
  };
  embassy: {
    city: string;
    address: string;
    appointmentWaitWeeks: string;
    phone?: string;
    website?: string;
  };
  documents: {
    specific: string[];
    bankOptions: string[];
  };
  financialProof: {
    monthlyEUR: number;
    blockedAccountEUR: number;
  };
  processingTime: {
    minWeeks: number;
    maxWeeks: number;
  };
  popularPrograms: string[];
  commonQuestions: Array<{ q: string; a: string }>;
  heroSubtitle: string;
}

export const countries: CountryData[] = [
  // ============================================================
  // NORTH AMERICA — INDIVIDUAL BUILDS
  // ============================================================
  {
    code: "US",
    name: "United States",
    flag: "🇺🇸",
    demonym: "American",
    currency: { code: "USD", symbol: "$", exchangeRate: 1.09 },
    embassy: {
      city: "Washington, DC",
      address: "3524 International Court NW, Washington, DC 20008",
      appointmentWaitWeeks: "~2 weeks",
      website: "https://www.bmeia.gv.at/en/embassy/washington",
    },
    documents: {
      specific: [
        "FBI Identity History Summary (3-5 business days online)",
        "State-level birth certificate + Secretary of State apostille",
        "US Department of State apostille for federal documents",
      ],
      bankOptions: [
        "US bank statements in USD accepted",
        "Chase/BofA international wire to your own account",
        "Wise EUR transfer",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 2, maxWeeks: 4 },
    popularPrograms: ["Music", "Psychology", "Engineering", "Arts", "Business"],
    commonQuestions: [
      { q: "Do I need a Visa D to enter Austria?", a: "No. US citizens enter Austria visa-free for up to 90 days. You can apply for your residence permit at the Austrian embassy before travel (safer) or at MA35/BH after arrival (faster start, but riskier if processing exceeds 90 days)." },
      { q: "How do I get the FBI background check?", a: "Apply online at identityhfs.fbi.gov. Processing takes 3-5 business days. The result must be apostilled by the US Department of State, then translated to German." },
    ],
    heroSubtitle: "Your Austrian residence permit, built for American students",
  },
  {
    code: "CA",
    name: "Canada",
    flag: "🇨🇦",
    demonym: "Canadian",
    currency: { code: "CAD", symbol: "C$", exchangeRate: 1.48 },
    embassy: {
      city: "Ottawa",
      address: "445 Wilbrod Street, Ottawa, ON K1N 6M7",
      appointmentWaitWeeks: "~2 weeks",
      website: "https://www.bmeia.gv.at/en/embassy/ottawa",
    },
    documents: {
      specific: [
        "RCMP Criminal Record Check (CPIC), 2-4 weeks",
        "Provincial birth certificate + provincial apostille",
        "Global Affairs Canada apostille for federal documents",
      ],
      bankOptions: [
        "Canadian bank statements in CAD accepted",
        "RBC/TD international wire to your own account",
        "Wise EUR transfer",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 2, maxWeeks: 4 },
    popularPrograms: ["Business", "Engineering", "Music", "Environmental Science"],
    commonQuestions: [
      { q: "Do I need a Visa D?", a: "No. Canadian citizens enter Austria visa-free for up to 90 days. You can apply at the Austrian Embassy in Ottawa or at MA35 after arrival." },
      { q: "How long does the RCMP check take?", a: "The RCMP Criminal Record Check takes 2-4 weeks. If using a private fingerprinting agency, factor in additional processing time." },
    ],
    heroSubtitle: "Your Austrian residence permit, built for Canadian students",
  },
  {
    code: "PK",
    name: "Pakistan",
    flag: "🇵🇰",
    demonym: "Pakistani",
    currency: { code: "PKR", symbol: "₨", exchangeRate: 305 },
    embassy: {
      city: "Islamabad",
      address: "Wagramer Strasse 2/24, Islamabad",
      appointmentWaitWeeks: "~4 weeks",
      website: "https://www.bmeia.gv.at/en/embassy/islamabad",
    },
    documents: {
      specific: [
        "HEC attestation + full legalisation (allow 2-3 weeks)",
        "IBCC equivalency certificate",
        "Police clearance from local police station",
      ],
      bankOptions: [
        "Meezan Bank direct EUR transfers",
        "HBL international wire",
        "Faysal Bank foreign exchange",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["Business Administration", "Computer Science", "Engineering", "Economics"],
    commonQuestions: [
      { q: "Do I need HEC attestation for Austrian universities?", a: "Yes, all Pakistani academic documents must be attested by HEC and then fully legalised for Austria. Austria does not accept apostilles from Pakistan. This process takes 2-3 weeks, so start early." },
      { q: "Do I need a blocked account, and how do I move the money?", a: "No blocked account is needed for Austria. The money can sit in a bank account in your name that you can use from Austria. If you convert PKR to EUR, Meezan Bank and HBL offer competitive rates for student transfers." },
      { q: "Is the Austrian Embassy in Islamabad the only option?", a: "Yes, for Pakistani citizens the Austrian Embassy in Islamabad handles all student visa applications. Book your appointment 4+ weeks in advance." },
      { q: "Do I need the VFS competence check?", a: "Yes, for most big universities. If your last school or degree is from Pakistan, the University of Vienna, TU Graz, the University of Graz, the University of Klagenfurt, BOKU and Montanuniversität Leoben ask you to pass a competence check at VFS Global before you apply, from winter semester 2026/27 (JKU Linz from summer semester 2027). You take it in person, it costs around €100 and it covers general knowledge in up to seven subjects. Our blog guide on the VFS competence check lists every university and the booking steps." },
      { q: "Do I need a visa consultant for Austria?", a: "No. Most students apply themselves: universities take applications online and you hand in the residence permit application in person at the Austrian embassy. Walk away from any agent who promises a visa or offers to put money in your account. Our blog guide on visa consultants lists every red flag." },
    ],
    heroSubtitle: "Your path from Islamabad to Vienna",
  },
  {
    code: "IN",
    name: "India",
    flag: "🇮🇳",
    demonym: "Indian",
    currency: { code: "INR", symbol: "₹", exchangeRate: 90 },
    embassy: {
      city: "New Delhi",
      address: "Chanakya Puri, New Delhi 110021",
      appointmentWaitWeeks: "~3 weeks",
      website: "https://www.bmeia.gv.at/en/embassy/new-delhi",
    },
    documents: {
      specific: [
        "MEA apostille + document attestation (1-2 weeks)",
        "WES/ENIC-NARIC equivalency evaluation",
        "Police Clearance Certificate from Passport Office",
      ],
      bankOptions: [
        "SBI EUR transfer to your own account",
        "Axis Bank international wire",
        "ICICI forex services",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Data Science", "Computer Science", "Economics", "Engineering"],
    commonQuestions: [
      { q: "Do I need MEA apostille for my Indian documents?", a: "Yes. All educational documents need MEA (Ministry of External Affairs) apostille. You can apply online through the MEA portal. Processing takes 1-2 weeks." },
      { q: "Which Indian banks are good for EUR transfers?", a: "SBI, Axis Bank and ICICI all offer international wire transfers in EUR. SBI typically has the most competitive forex rates for students." },
      { q: "Do I need the VFS competence check?", a: "Yes, for most big universities. If your last school or degree is from India, the University of Vienna, TU Graz, the University of Graz, the University of Klagenfurt, BOKU and Montanuniversität Leoben ask you to pass a competence check at VFS Global before you apply, from winter semester 2026/27 (JKU Linz from summer semester 2027). You take it in person, it costs around €100 and it covers general knowledge in up to seven subjects. Our blog guide on the VFS competence check lists every university and the booking steps." },
      { q: "Do I need a visa consultant for Austria?", a: "No. Most students apply themselves: universities take applications online and you hand in the residence permit application in person at the Austrian embassy. Walk away from any agent who promises a visa or offers to put money in your account. Our blog guide on visa consultants lists every red flag." },
    ],
    heroSubtitle: "Your path from New Delhi to Vienna",
  },
  {
    code: "TR",
    name: "Turkey",
    flag: "🇹🇷",
    demonym: "Turkish",
    currency: { code: "TRY", symbol: "₺", exchangeRate: 35 },
    embassy: {
      city: "Ankara",
      address: "Atatürk Bulvarı 189, Ankara",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "YÖK equivalency certificate",
        "Apostille from Turkish government",
        "Police clearance (Adli Sicil Kaydı)",
      ],
      bankOptions: [
        "İş Bankası EUR transfers",
        "Garanti BBVA international wire",
        "Ziraat Bankası forex",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Architecture", "Business", "Medicine"],
    commonQuestions: [
      { q: "Do I need YÖK equivalency?", a: "If your Turkish degree needs recognition in Austria, you may need YÖK equivalency. Austrian universities usually evaluate Turkish transcripts directly, but check with your specific program." },
    ],
    heroSubtitle: "Your path from Ankara to Vienna",
  },
  {
    code: "NG",
    name: "Nigeria",
    flag: "🇳🇬",
    demonym: "Nigerian",
    currency: { code: "NGN", symbol: "₦", exchangeRate: 1650 },
    embassy: {
      city: "Abuja",
      address: "Plot 7, Usuma Street, Maitama, Abuja",
      appointmentWaitWeeks: "~5 weeks",
    },
    documents: {
      specific: [
        "WAEC/NECO verification (3-4 weeks)",
        "Credential evaluation from WES",
        "Police clearance from NPF",
      ],
      bankOptions: [
        "Zenith Bank EUR international transfer",
        "GTBank foreign exchange",
        "Access Bank international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 10, maxWeeks: 14 },
    popularPrograms: ["Business Administration", "Computer Science", "Engineering", "Law"],
    commonQuestions: [
      { q: "How long does WAEC verification take?", a: "WAEC verification typically takes 3-4 weeks. Start this process as soon as you begin your application. You'll need the original scratch card for online verification." },
      { q: "Can I send the money I need to show from Nigeria?", a: "Yes, and you do not need a blocked account for Austria, but Nigerian forex regulations can make large EUR transfers complex. Zenith Bank and GTBank have the most reliable international transfer services for students." },
    ],
    heroSubtitle: "Your path from Abuja to Vienna",
  },
  {
    code: "KZ",
    name: "Kazakhstan",
    flag: "🇰🇿",
    demonym: "Kazakh",
    currency: { code: "KZT", symbol: "₸", exchangeRate: 505 },
    embassy: {
      city: "Astana",
      address: "Kosmonavtov St 62, Astana",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education apostille",
        "Notarized translation to German",
        "Police clearance certificate",
      ],
      bankOptions: [
        "Kaspi Bank EUR transfer",
        "Halyk Bank international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Business", "IT", "Natural Sciences"],
    commonQuestions: [
      { q: "Do Kazakh degrees need additional recognition?", a: "Austrian universities generally recognize Kazakh higher education. You'll need apostilled and notarized German translations of your documents." },
    ],
    heroSubtitle: "Your path from Astana to Vienna",
  },
  {
    code: "BD",
    name: "Bangladesh",
    flag: "🇧🇩",
    demonym: "Bangladeshi",
    currency: { code: "BDT", symbol: "৳", exchangeRate: 118 },
    embassy: {
      city: "New Delhi",
      address: "Austrian Embassy New Delhi (covers Bangladesh)",
      appointmentWaitWeeks: "~4 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education attestation + full legalisation (2-3 weeks)",
        "Board exam certificate verification",
        "Police clearance from CID",
      ],
      bankOptions: [
        "Standard Chartered BD EUR transfers",
        "BRAC Bank international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["Engineering", "Business", "Computer Science", "Economics"],
    commonQuestions: [
      { q: "Which embassy handles Bangladeshi applications?", a: "The Austrian Embassy in New Delhi handles visa applications for Bangladeshi citizens. You'll need to travel to Delhi for your appointment." },
      { q: "Do I need the VFS competence check?", a: "Yes, for most big universities. If your last school or degree is from Bangladesh, the University of Vienna, TU Graz, the University of Graz, the University of Klagenfurt, BOKU and Montanuniversität Leoben ask you to pass a competence check at VFS Global before you apply, from winter semester 2026/27 (JKU Linz from summer semester 2027). You take it in person, it costs around €100 and it covers general knowledge in up to seven subjects. Our blog guide on the VFS competence check lists every university and the booking steps." },
      { q: "Do I need a visa consultant for Austria?", a: "No. Most students apply themselves: universities take applications online and you hand in the residence permit application in person at the Austrian embassy. Walk away from any agent who promises a visa or offers to put money in your account. Our blog guide on visa consultants lists every red flag." },
    ],
    heroSubtitle: "Your path from Dhaka to Vienna",
  },
  {
    code: "EG",
    name: "Egypt",
    flag: "🇪🇬",
    demonym: "Egyptian",
    currency: { code: "EGP", symbol: "E£", exchangeRate: 52 },
    embassy: {
      city: "Cairo",
      address: "5 Wissa Wassef Street, Riyadh Tower, Cairo",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Ministry of Foreign Affairs attestation (2 weeks)",
        "Supreme Council of Universities equivalency",
        "Police clearance from Ministry of Interior",
      ],
      bankOptions: [
        "CIB Egypt EUR transfer services",
        "National Bank of Egypt international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Medicine", "Engineering", "Business", "Architecture"],
    commonQuestions: [
      { q: "Do Egyptian degrees need special recognition?", a: "Egyptian higher education is generally recognized in Austria. You'll need attestation from the Ministry of Foreign Affairs and potentially a Supreme Council equivalency letter." },
    ],
    heroSubtitle: "Your path from Cairo to Vienna",
  },
  {
    code: "CN",
    name: "China",
    flag: "🇨🇳",
    demonym: "Chinese",
    currency: { code: "CNY", symbol: "¥", exchangeRate: 7.8 },
    embassy: {
      city: "Beijing",
      address: "Jianguomenwai Dajie 5, Beijing",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "CHSI verification + notarization + apostille (2-3 weeks)",
        "Gaokao/degree certificate notarization",
        "Police clearance from local PSB",
      ],
      bankOptions: [
        "Bank of China direct EUR transfer",
        "ICBC international transfer",
        "China Merchants Bank forex",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 8 },
    popularPrograms: ["Music", "Engineering", "Business", "Data Science"],
    commonQuestions: [
      { q: "Is CHSI verification mandatory?", a: "Yes, Chinese students must verify their educational credentials through CHSI (China Higher Education Student Information). This takes 2-3 weeks and is required before the apostille process." },
    ],
    heroSubtitle: "Your path from Beijing to Vienna",
  },
  {
    code: "VN",
    name: "Vietnam",
    flag: "🇻🇳",
    demonym: "Vietnamese",
    currency: { code: "VND", symbol: "₫", exchangeRate: 26500 },
    embassy: {
      city: "Hanoi",
      address: "Lot E4, Trung Tu Diplomatic Quarter, Hanoi",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education certification + full legalisation",
        "Notarized Vietnamese-German translation",
        "Police clearance from local authority",
      ],
      bankOptions: [
        "Vietcombank EUR transfer",
        "BIDV international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "IT", "Business", "Natural Sciences"],
    commonQuestions: [
      { q: "Are Vietnamese degrees recognized in Austria?", a: "Most Vietnamese bachelor's and master's degrees are recognized. You will need fully legalised documents (Austria does not accept apostilles from Vietnam) and notarized translations. Some programs may require additional credential evaluation." },
    ],
    heroSubtitle: "Your path from Hanoi to Vienna",
  },
  {
    code: "MA",
    name: "Morocco",
    flag: "🇲🇦",
    demonym: "Moroccan",
    currency: { code: "MAD", symbol: "MAD", exchangeRate: 11 },
    embassy: {
      city: "Rabat",
      address: "2 Zankat Tiddas, Rabat",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Ministry of Foreign Affairs legalization",
        "Baccalauréat attestation",
        "Police clearance (Casier Judiciaire)",
      ],
      bankOptions: [
        "Attijariwafa Bank EUR transfers",
        "BMCE Bank international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Business", "Engineering", "Languages", "Architecture"],
    commonQuestions: [
      { q: "Do French-language documents need German translation?", a: "Yes, all French-language documents must be translated into German by a certified translator and then notarized." },
    ],
    heroSubtitle: "Your path from Rabat to Vienna",
  },
  {
    code: "KE",
    name: "Kenya",
    flag: "🇰🇪",
    demonym: "Kenyan",
    currency: { code: "KES", symbol: "KSh", exchangeRate: 155 },
    embassy: {
      city: "Nairobi",
      address: "City Square, Nairobi",
      appointmentWaitWeeks: "~4 weeks",
    },
    documents: {
      specific: [
        "KNEC verification + full legalisation",
        "University certificate attestation",
        "Police clearance from DCI",
      ],
      bankOptions: [
        "KCB EUR international transfer",
        "Equity Bank forex services",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["Business", "Engineering", "IT", "Environmental Science"],
    commonQuestions: [
      { q: "Do I need KNEC verification?", a: "Yes, secondary school certificates from KNEC need to be verified. University transcripts should be certified by your institution. Both need full legalisation, because Kenya is not part of the Apostille Convention." },
    ],
    heroSubtitle: "Your path from Nairobi to Vienna",
  },
  {
    code: "BR",
    name: "Brazil",
    flag: "🇧🇷",
    demonym: "Brazilian",
    currency: { code: "BRL", symbol: "R$", exchangeRate: 5.5 },
    embassy: {
      city: "Brasília",
      address: "SES Quadra 811, Brasília",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Consular legalization + apostille",
        "Sworn translation (tradução juramentada)",
        "Federal Police clearance",
      ],
      bankOptions: [
        "Banco do Brasil EUR transfer",
        "Itaú international wire",
        "Wise/Remessa Online",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Music", "Engineering", "Architecture", "Business"],
    commonQuestions: [
      { q: "Are Brazilian apostille and sworn translation required?", a: "Yes. Brazil is part of the Hague Apostille Convention. All documents need apostille from a cartório and sworn translation (tradução juramentada) into German." },
    ],
    heroSubtitle: "Your path from Brasília to Vienna",
  },
  {
    code: "IR",
    name: "Iran",
    flag: "🇮🇷",
    demonym: "Iranian",
    currency: { code: "IRR", symbol: "﷼", exchangeRate: 57000 },
    embassy: {
      city: "Tehran",
      address: "Sar-e Kucheh Bist, Tehran",
      appointmentWaitWeeks: "~4 weeks",
    },
    documents: {
      specific: [
        "Ministry of Foreign Affairs attestation",
        "Konkor exam certification",
        "Police clearance from local judiciary",
      ],
      bankOptions: [
        "Due to sanctions, alternative transfer methods required",
        "Ask your bank which transfer routes to the EU are allowed for you",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Engineering", "Medicine", "Computer Science", "Physics"],
    commonQuestions: [
      { q: "How do I handle the financial transfer from Iran?", a: "Due to international sanctions, direct bank transfers from Iran can be complicated. Many Iranian students use intermediary accounts in Turkey or the UAE. Contact us for detailed guidance." },
    ],
    heroSubtitle: "Your path from Tehran to Vienna",
  },
  {
    code: "RU",
    name: "Russia",
    flag: "🇷🇺",
    demonym: "Russian",
    currency: { code: "RUB", symbol: "₽", exchangeRate: 100 },
    embassy: {
      city: "Moscow",
      address: "Starokonjuschenny per. 1, Moscow",
      appointmentWaitWeeks: "~4 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education apostille",
        "Notarized German translation of all documents",
        "Police clearance from Ministry of Interior",
      ],
      bankOptions: [
        "Alternative transfer methods required due to sanctions",
        "Ask your bank which transfer routes to the EU are allowed for you",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Computer Science", "Medicine", "Music"],
    commonQuestions: [
      { q: "How do I handle financial transfers from Russia?", a: "Due to current sanctions, direct bank transfers can be complex. Austria does not require a blocked account, but the money must be usable from Austria, so many students keep it at a bank outside Russia. Ask your bank how to move it legally." },
    ],
    heroSubtitle: "Your path to Vienna",
  },
  {
    code: "UA",
    name: "Ukraine",
    flag: "🇺🇦",
    demonym: "Ukrainian",
    currency: { code: "UAH", symbol: "₴", exchangeRate: 42 },
    embassy: {
      city: "Kyiv",
      address: "Ivana Franka St. 33, Kyiv",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education apostille",
        "Notarized German translation",
        "Police clearance certificate",
      ],
      bankOptions: [
        "PrivatBank EUR transfer",
        "Monobank international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "IT", "Medicine", "Business"],
    commonQuestions: [
      { q: "Do I need a student visa if I have temporary protection?", a: "If you already hold temporary protection status in Austria, you may not need a separate student residence permit. Ask us in the community and we help you work out which applies." },
    ],
    heroSubtitle: "Your path to Vienna",
  },
  {
    code: "CO",
    name: "Colombia",
    flag: "🇨🇴",
    demonym: "Colombian",
    currency: { code: "COP", symbol: "$", exchangeRate: 4400 },
    embassy: {
      city: "Bogotá",
      address: "Carrera 9 No. 73-44, Bogotá",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Apostille from Cancillería",
        "Sworn translation to German",
        "Police clearance from Policía Nacional",
      ],
      bankOptions: [
        "Bancolombia EUR transfer",
        "Davivienda international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "Engineering", "Music", "Architecture"],
    commonQuestions: [
      { q: "Is the Colombian apostille enough?", a: "Yes, Colombia is part of the Hague Convention. An apostille from the Cancillería plus a certified German translation is sufficient." },
    ],
    heroSubtitle: "Your path from Bogotá to Vienna",
  },
  {
    code: "ID",
    name: "Indonesia",
    flag: "🇮🇩",
    demonym: "Indonesian",
    currency: { code: "IDR", symbol: "Rp", exchangeRate: 17000 },
    embassy: {
      city: "Jakarta",
      address: "Jl. Diponegoro 44, Jakarta",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Kemendikbud legalization",
        "Notarized German translation",
        "Police clearance (SKCK)",
      ],
      bankOptions: [
        "Bank Mandiri EUR transfer",
        "BCA international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Business", "IT", "Environmental Science"],
    commonQuestions: [
      { q: "Do Indonesian documents need Kemendikbud legalization?", a: "Yes, educational documents need legalization from the Ministry of Education (Kemendikbud) before the apostille process." },
    ],
    heroSubtitle: "Your path from Jakarta to Vienna",
  },
  // ============================================================
  // ARABIC SEGMENT COUNTRIES
  // ============================================================
  {
    code: "SA",
    name: "Saudi Arabia",
    flag: "🇸🇦",
    demonym: "Saudi",
    currency: { code: "SAR", symbol: "﷼", exchangeRate: 4.1 },
    embassy: {
      city: "Riyadh",
      address: "Diplomatic Quarter, Riyadh",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education attestation",
        "Notarized German translation of all documents",
        "Police clearance from local authority",
      ],
      bankOptions: [
        "Al Rajhi Bank EUR transfer",
        "Saudi National Bank international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Business", "Medicine", "Computer Science"],
    commonQuestions: [
      { q: "Does the Saudi Cultural Mission cover Austria?", a: "Yes, the Saudi Arabian Cultural Mission (SACM) supports students in Austria. Check with your scholarship provider for specific coverage details." },
    ],
    heroSubtitle: "Your path from Riyadh to Vienna",
  },
  {
    code: "AE",
    name: "United Arab Emirates",
    flag: "🇦🇪",
    demonym: "Emirati",
    currency: { code: "AED", symbol: "د.إ", exchangeRate: 4.0 },
    embassy: {
      city: "Abu Dhabi",
      address: "Al Nahyan Camp Area, Abu Dhabi",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education attestation",
        "Notarized German translation",
        "Police clearance certificate",
      ],
      bankOptions: [
        "Emirates NBD EUR transfer",
        "ADCB international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "Engineering", "Architecture", "Medicine"],
    commonQuestions: [
      { q: "Can I apply from the Austrian Embassy in Abu Dhabi?", a: "Yes, the Austrian Embassy in Abu Dhabi handles all visa applications for UAE residents." },
    ],
    heroSubtitle: "Your path from Abu Dhabi to Vienna",
  },
  {
    code: "IQ",
    name: "Iraq",
    flag: "🇮🇶",
    demonym: "Iraqi",
    currency: { code: "IQD", symbol: "ع.د", exchangeRate: 1430 },
    embassy: {
      city: "Baghdad",
      address: "Austrian Embassy Baghdad",
      appointmentWaitWeeks: "~4 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized German translation",
        "Police clearance from local authority",
      ],
      bankOptions: [
        "Rasheed Bank EUR transfer",
        "Trade Bank of Iraq international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Medicine", "Engineering", "IT", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path from Baghdad to Vienna",
  },
  {
    code: "JO",
    name: "Jordan",
    flag: "🇯🇴",
    demonym: "Jordanian",
    currency: { code: "JOD", symbol: "د.ا", exchangeRate: 0.77 },
    embassy: {
      city: "Amman",
      address: "Kherbet Al Souq, Amman",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized German translation",
        "Police clearance from Public Security Directorate",
      ],
      bankOptions: [
        "Arab Bank EUR transfer",
        "Housing Bank international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Medicine", "Business", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Amman to Vienna",
  },
  {
    code: "SY",
    name: "Syria",
    flag: "🇸🇾",
    demonym: "Syrian",
    currency: { code: "SYP", symbol: "£S", exchangeRate: 14000 },
    embassy: {
      city: "Damascus",
      address: "Austrian Embassy (via Beirut representation)",
      appointmentWaitWeeks: "~6 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized German translation",
        "Police clearance certificate",
      ],
      bankOptions: [
        "Alternative transfer methods may be required",
        "Ask your bank which transfer routes to the EU are allowed for you",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Medicine", "Engineering", "Pharmacy", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path to Vienna",
  },
  {
    code: "LB",
    name: "Lebanon",
    flag: "🇱🇧",
    demonym: "Lebanese",
    currency: { code: "LBP", symbol: "ل.ل", exchangeRate: 98000 },
    embassy: {
      city: "Beirut",
      address: "Wata El Mousaitbeh, Beirut",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education attestation",
        "Notarized German translation",
        "Police clearance from General Security",
      ],
      bankOptions: [
        "Bank Audi EUR transfer (where available)",
        "Alternative transfer services",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Business", "Engineering", "Medicine", "Architecture"],
    commonQuestions: [],
    heroSubtitle: "Your path from Beirut to Vienna",
  },
  {
    code: "TN",
    name: "Tunisia",
    flag: "🇹🇳",
    demonym: "Tunisian",
    currency: { code: "TND", symbol: "د.ت", exchangeRate: 3.4 },
    embassy: {
      city: "Tunis",
      address: "Rue Ben Ghedhahem, Tunis",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized German or French translation",
        "Police clearance (Bulletin N°3)",
      ],
      bankOptions: [
        "BIAT EUR transfer",
        "Banque de Tunisie international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Business", "IT", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Tunis to Vienna",
  },
  {
    code: "DZ",
    name: "Algeria",
    flag: "🇩🇿",
    demonym: "Algerian",
    currency: { code: "DZD", symbol: "د.ج", exchangeRate: 147 },
    embassy: {
      city: "Algiers",
      address: "Austrian Embassy Algiers",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized French or German translation",
        "Police clearance (Casier Judiciaire)",
      ],
      bankOptions: [
        "BNA EUR transfer",
        "CPA international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Medicine", "Business", "Architecture"],
    commonQuestions: [],
    heroSubtitle: "Your path from Algiers to Vienna",
  },
  {
    code: "KW",
    name: "Kuwait",
    flag: "🇰🇼",
    demonym: "Kuwaiti",
    currency: { code: "KWD", symbol: "د.ك", exchangeRate: 0.33 },
    embassy: {
      city: "Kuwait City",
      address: "Austrian Embassy Kuwait",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized German translation",
        "Police clearance certificate",
      ],
      bankOptions: [
        "National Bank of Kuwait EUR transfer",
        "Kuwait Finance House international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "Engineering", "Medicine", "Law"],
    commonQuestions: [],
    heroSubtitle: "Your path from Kuwait City to Vienna",
  },
  {
    code: "OM",
    name: "Oman",
    flag: "🇴🇲",
    demonym: "Omani",
    currency: { code: "OMR", symbol: "ر.ع", exchangeRate: 0.42 },
    embassy: {
      city: "Mascat",
      address: "Austrian Embassy Muscat",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized German translation",
        "Police clearance from Royal Oman Police",
      ],
      bankOptions: [
        "Bank Muscat EUR transfer",
        "National Bank of Oman international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Business", "Medicine", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Muscat to Vienna",
  },
  {
    code: "QA",
    name: "Qatar",
    flag: "🇶🇦",
    demonym: "Qatari",
    currency: { code: "QAR", symbol: "ر.ق", exchangeRate: 3.97 },
    embassy: {
      city: "Doha",
      address: "Austrian Embassy Doha",
      appointmentWaitWeeks: "~2 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education attestation",
        "Notarized German translation",
        "Police clearance certificate",
      ],
      bankOptions: [
        "QNB EUR transfer",
        "Commercial Bank of Qatar international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "Engineering", "Architecture", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Doha to Vienna",
  },
  {
    code: "BH",
    name: "Bahrain",
    flag: "🇧🇭",
    demonym: "Bahraini",
    currency: { code: "BHD", symbol: "ب.د", exchangeRate: 0.41 },
    embassy: {
      city: "Manama",
      address: "Austrian Embassy (via Riyadh representation)",
      appointmentWaitWeeks: "~3 weeks",
    },
    documents: {
      specific: [
        "Ministry of Education attestation",
        "Notarized German translation",
        "Police clearance certificate",
      ],
      bankOptions: [
        "National Bank of Bahrain EUR transfer",
        "Bank of Bahrain and Kuwait international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "Engineering", "Medicine", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Manama to Vienna",
  },
  {
    code: "YE",
    name: "Yemen",
    flag: "🇾🇪",
    demonym: "Yemeni",
    currency: { code: "YER", symbol: "ر.ي", exchangeRate: 273 },
    embassy: {
      city: "Sana'a",
      address: "Austrian Embassy (via Riyadh representation)",
      appointmentWaitWeeks: "~6 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized German translation",
        "Police clearance certificate",
      ],
      bankOptions: [
        "Alternative transfer methods may be required",
        "Ask your bank which transfer routes to the EU are allowed for you",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Medicine", "Engineering", "IT", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path to Vienna",
  },
  {
    code: "LY",
    name: "Libya",
    flag: "🇱🇾",
    demonym: "Libyan",
    currency: { code: "LYD", symbol: "ل.د", exchangeRate: 5.3 },
    embassy: {
      city: "Tripoli",
      address: "Austrian Embassy (via Tunis representation)",
      appointmentWaitWeeks: "~4 weeks",
    },
    documents: {
      specific: [
        "Ministry of Higher Education attestation",
        "Notarized German translation",
        "Police clearance certificate",
      ],
      bankOptions: [
        "Bank of Libya EUR transfer",
        "Wahda Bank international wire",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["Engineering", "Medicine", "Business", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path to Vienna",
  },
  // ============================================================
  // WESTERN BALKANS — major source countries for Austria
  // ============================================================
  {
    code: "RS",
    name: "Serbia",
    flag: "🇷🇸",
    demonym: "Serbian",
    currency: { code: "RSD", symbol: "дін", exchangeRate: 117 },
    embassy: { city: "Belgrade", address: "Kneza Sime Markovića 2, Belgrade", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance (Uverenje iz kaznene evidencije)"], bankOptions: ["Banca Intesa EUR transfer", "UniCredit Serbia international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Business", "IT", "Medicine"],
    commonQuestions: [{ q: "Do Serbian degrees have automatic recognition?", a: "Serbia has bilateral education agreements with Austria. Most degrees are recognized, but you still need apostilled and translated documents." }],
    heroSubtitle: "Your path from Belgrade to Vienna",
  },
  {
    code: "BA",
    name: "Bosnia and Herzegovina",
    flag: "🇧🇦",
    demonym: "Bosnian",
    currency: { code: "BAM", symbol: "KM", exchangeRate: 1.96 },
    embassy: { city: "Sarajevo", address: "Džidžikovac 7, Sarajevo", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance from MUP"], bankOptions: ["UniCredit BH EUR transfer", "Raiffeisen Bank BH international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Business", "Architecture", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Sarajevo to Vienna",
  },
  {
    code: "XK",
    name: "Kosovo",
    flag: "🇽🇰",
    demonym: "Kosovar",
    currency: { code: "EUR", symbol: "€", exchangeRate: 1 },
    embassy: { city: "Pristina", address: "Austrian Embassy Pristina", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education attestation", "Notarized German translation", "Police clearance certificate"], bankOptions: ["ProCredit Bank EUR transfer", "Raiffeisen Bank Kosovo international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "IT", "Law", "Engineering"],
    commonQuestions: [],
    heroSubtitle: "Your path from Pristina to Vienna",
  },
  {
    code: "AL",
    name: "Albania",
    flag: "🇦🇱",
    demonym: "Albanian",
    currency: { code: "ALL", symbol: "L", exchangeRate: 104 },
    embassy: { city: "Tirana", address: "Rruga Frederik Shiroka, Tirana", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance (Dëshmi Penaliteti)"], bankOptions: ["Raiffeisen Bank Albania EUR transfer", "BKT international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Medicine", "Engineering", "Business", "Architecture"],
    commonQuestions: [],
    heroSubtitle: "Your path from Tirana to Vienna",
  },
  {
    code: "MK",
    name: "North Macedonia",
    flag: "🇲🇰",
    demonym: "Macedonian",
    currency: { code: "MKD", symbol: "ден", exchangeRate: 61.5 },
    embassy: { city: "Skopje", address: "Austrian Embassy Skopje", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance certificate"], bankOptions: ["Komercijalna Banka EUR transfer", "Stopanska Banka international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["IT", "Engineering", "Business", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Skopje to Vienna",
  },
  {
    code: "ME",
    name: "Montenegro",
    flag: "🇲🇪",
    demonym: "Montenegrin",
    currency: { code: "EUR", symbol: "€", exchangeRate: 1 },
    embassy: { city: "Podgorica", address: "Austrian Embassy Podgorica", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance certificate"], bankOptions: ["CKB EUR transfer", "NLB Montenegro international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "IT", "Tourism", "Engineering"],
    commonQuestions: [],
    heroSubtitle: "Your path from Podgorica to Vienna",
  },
  {
    code: "MD",
    name: "Moldova",
    flag: "🇲🇩",
    demonym: "Moldovan",
    currency: { code: "MDL", symbol: "L", exchangeRate: 19.4 },
    embassy: { city: "Chișinău", address: "Austrian Embassy (via Bucharest representation)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance from Ministry of Interior"], bankOptions: ["Moldova Agroindbank EUR transfer", "Moldindconbank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["IT", "Medicine", "Business", "Engineering"],
    commonQuestions: [],
    heroSubtitle: "Your path from Chișinău to Vienna",
  },
  // ============================================================
  // CAUCASUS & CENTRAL ASIA
  // ============================================================
  {
    code: "GE",
    name: "Georgia",
    flag: "🇬🇪",
    demonym: "Georgian",
    currency: { code: "GEL", symbol: "₾", exchangeRate: 2.95 },
    embassy: { city: "Tbilisi", address: "Austrian Embassy Tbilisi", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance from Ministry of Interior"], bankOptions: ["Bank of Georgia EUR transfer", "TBC Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "IT", "Medicine", "Engineering"],
    commonQuestions: [],
    heroSubtitle: "Your path from Tbilisi to Vienna",
  },
  {
    code: "AM",
    name: "Armenia",
    flag: "🇦🇲",
    demonym: "Armenian",
    currency: { code: "AMD", symbol: "֏", exchangeRate: 420 },
    embassy: { city: "Yerevan", address: "Austrian Embassy (via Tbilisi representation)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance from police department"], bankOptions: ["Ameriabank EUR transfer", "Ardshinbank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["IT", "Engineering", "Medicine", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path from Yerevan to Vienna",
  },
  {
    code: "AZ",
    name: "Azerbaijan",
    flag: "🇦🇿",
    demonym: "Azerbaijani",
    currency: { code: "AZN", symbol: "₼", exchangeRate: 1.85 },
    embassy: { city: "Baku", address: "Austrian Embassy Baku", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance certificate"], bankOptions: ["Kapital Bank EUR transfer", "PASHA Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Business", "IT", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Baku to Vienna",
  },
  {
    code: "UZ",
    name: "Uzbekistan",
    flag: "🇺🇿",
    demonym: "Uzbek",
    currency: { code: "UZS", symbol: "сўм", exchangeRate: 13700 },
    embassy: { city: "Tashkent", address: "Austrian Embassy (via Astana representation)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Ministry of Higher Education certification + full legalisation", "Notarized German translation", "Police clearance certificate"], bankOptions: ["NBU EUR transfer", "Ipak Yuli Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Business", "Medicine", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Tashkent to Vienna",
  },
  {
    code: "KG",
    name: "Kyrgyzstan",
    flag: "🇰🇬",
    demonym: "Kyrgyz",
    currency: { code: "KGS", symbol: "сом", exchangeRate: 96 },
    embassy: { city: "Bishkek", address: "Austrian Embassy (via Astana representation)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Ministry of Education certification + full legalisation", "Notarized German translation", "Police clearance certificate"], bankOptions: ["Optima Bank EUR transfer", "Demir Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Business", "IT", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Bishkek to Vienna",
  },
  {
    code: "TJ",
    name: "Tajikistan",
    flag: "🇹🇯",
    demonym: "Tajik",
    currency: { code: "TJS", symbol: "SM", exchangeRate: 11.8 },
    embassy: { city: "Dushanbe", address: "Austrian Embassy (via Astana representation)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["Ministry of Education certification + full legalisation", "Notarized German translation", "Police clearance certificate"], bankOptions: ["Orienbank EUR transfer", "Agroinvestbank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 12 },
    popularPrograms: ["Engineering", "Medicine", "Business", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Dushanbe to Vienna",
  },
  {
    code: "TM",
    name: "Turkmenistan",
    flag: "🇹🇲",
    demonym: "Turkmen",
    currency: { code: "TMT", symbol: "m", exchangeRate: 3.82 },
    embassy: { city: "Ashgabat", address: "Austrian Embassy (via Astana representation)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["Ministry of Education certification + full legalisation", "Notarized German translation", "Police clearance certificate"], bankOptions: ["State Bank for Foreign Economic Affairs EUR transfer", "Ask your bank which transfer routes to the EU are allowed for you"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 12 },
    popularPrograms: ["Engineering", "Medicine", "IT", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path from Ashgabat to Vienna",
  },
  // ============================================================
  // SOUTH & SOUTHEAST ASIA
  // ============================================================
  {
    code: "NP",
    name: "Nepal",
    flag: "🇳🇵",
    demonym: "Nepali",
    currency: { code: "NPR", symbol: "रू", exchangeRate: 145 },
    embassy: { city: "New Delhi", address: "Austrian Embassy New Delhi (covers Nepal)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["Ministry of Education attestation + full legalisation", "SLC/SEE certificate verification", "Police clearance from Nepal Police HQ"], bankOptions: ["Nepal Rastra Bank EUR transfer", "Nabil Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["Engineering", "IT", "Business", "Medicine"],
    commonQuestions: [{ q: "Which embassy handles Nepali applications?", a: "The Austrian Embassy in New Delhi handles visa applications for Nepali citizens." }],
    heroSubtitle: "Your path from Kathmandu to Vienna",
  },
  {
    code: "LK",
    name: "Sri Lanka",
    flag: "🇱🇰",
    demonym: "Sri Lankan",
    currency: { code: "LKR", symbol: "Rs", exchangeRate: 325 },
    embassy: { city: "New Delhi", address: "Austrian Embassy New Delhi (covers Sri Lanka)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["Ministry of Education attestation", "UGC equivalency certificate", "Police clearance from CID"], bankOptions: ["Commercial Bank of Ceylon EUR transfer", "Sampath Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["Engineering", "IT", "Medicine", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path from Colombo to Vienna",
  },
  {
    code: "PH",
    name: "Philippines",
    flag: "🇵🇭",
    demonym: "Filipino",
    currency: { code: "PHP", symbol: "₱", exchangeRate: 62 },
    embassy: { city: "Manila", address: "Austrian Embassy Manila", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["DFA apostille", "Notarized German translation", "NBI clearance"], bankOptions: ["BDO EUR transfer", "BPI international wire", "Metrobank forex services"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Nursing Science", "Business", "Engineering", "IT"],
    commonQuestions: [{ q: "Do I need DFA apostille?", a: "Yes. The Philippines joined the Apostille Convention in 2019 and Austria has accepted Philippine apostilles since 2023, so you need an apostille from DFA instead of Red Ribbon authentication." }],
    heroSubtitle: "Your path from Manila to Vienna",
  },
  {
    code: "TH",
    name: "Thailand",
    flag: "🇹🇭",
    demonym: "Thai",
    currency: { code: "THB", symbol: "฿", exchangeRate: 38 },
    embassy: { city: "Bangkok", address: "Austrian Embassy Bangkok", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Foreign Affairs legalization", "Notarized German translation", "Police clearance from Royal Thai Police"], bankOptions: ["Bangkok Bank EUR transfer", "Kasikorn Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Business", "Hospitality", "Engineering", "Arts"],
    commonQuestions: [],
    heroSubtitle: "Your path from Bangkok to Vienna",
  },
  {
    code: "MY",
    name: "Malaysia",
    flag: "🇲🇾",
    demonym: "Malaysian",
    currency: { code: "MYR", symbol: "RM", exchangeRate: 4.8 },
    embassy: { city: "Kuala Lumpur", address: "Austrian Embassy Kuala Lumpur", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Ministry of Education attestation", "Notarized German translation", "Police clearance from PDRM"], bankOptions: ["Maybank EUR transfer", "CIMB international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Business", "IT", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Kuala Lumpur to Vienna",
  },
  {
    code: "MM",
    name: "Myanmar",
    flag: "🇲🇲",
    demonym: "Myanmar",
    currency: { code: "MMK", symbol: "K", exchangeRate: 2290 },
    embassy: { city: "Bangkok", address: "Austrian Embassy Bangkok (covers Myanmar)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["Ministry of Education attestation", "Notarized German translation", "Police clearance certificate"], bankOptions: ["KBZ Bank EUR transfer", "Alternative transfer methods may be needed"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Engineering", "IT", "Medicine", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path to Vienna",
  },
  {
    code: "MN",
    name: "Mongolia",
    flag: "🇲🇳",
    demonym: "Mongolian",
    currency: { code: "MNT", symbol: "₮", exchangeRate: 3740 },
    embassy: { city: "Beijing", address: "Austrian Embassy Beijing (covers Mongolia)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Ministry of Education certification + full legalisation", "Notarized German translation", "Police clearance from National Police"], bankOptions: ["Khan Bank EUR transfer", "Golomt Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "IT", "Business", "Natural Sciences"],
    commonQuestions: [],
    heroSubtitle: "Your path from Ulaanbaatar to Vienna",
  },
  // ============================================================
  // EAST ASIA
  // ============================================================
  {
    code: "KR",
    name: "South Korea",
    flag: "🇰🇷",
    demonym: "Korean",
    currency: { code: "KRW", symbol: "₩", exchangeRate: 1430 },
    embassy: { city: "Seoul", address: "Songwol-dong, Jongno-gu, Seoul", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Apostille from Ministry of Foreign Affairs", "Notarized German translation", "Police clearance from Korean National Police"], bankOptions: ["Hana Bank EUR transfer", "KB Kookmin Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 6 },
    popularPrograms: ["Music", "Engineering", "Business", "Arts"],
    commonQuestions: [],
    heroSubtitle: "Your path from Seoul to Vienna",
  },
  {
    code: "JP",
    name: "Japan",
    flag: "🇯🇵",
    demonym: "Japanese",
    currency: { code: "JPY", symbol: "¥", exchangeRate: 164 },
    embassy: { city: "Tokyo", address: "Minato-ku, Tokyo", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Apostille from Ministry of Foreign Affairs", "Notarized German translation", "Police clearance certificate"], bankOptions: ["SMBC EUR transfer", "MUFG international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 6 },
    popularPrograms: ["Music", "Arts", "Engineering", "Architecture"],
    commonQuestions: [],
    heroSubtitle: "Your path from Tokyo to Vienna",
  },
  {
    code: "TW",
    name: "Taiwan",
    flag: "🇹🇼",
    demonym: "Taiwanese",
    currency: { code: "TWD", symbol: "NT$", exchangeRate: 34.5 },
    embassy: { city: "Taipei", address: "Austrian Office Taipei", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["MOFA authentication + TECO verification", "Notarized German translation", "Police clearance from NPA"], bankOptions: ["Bank of Taiwan EUR transfer", "CTBC Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Music", "Business", "Design"],
    commonQuestions: [],
    heroSubtitle: "Your path from Taipei to Vienna",
  },
  // ============================================================
  // SUB-SAHARAN AFRICA
  // ============================================================
  {
    code: "GH",
    name: "Ghana",
    flag: "🇬🇭",
    demonym: "Ghanaian",
    currency: { code: "GHS", symbol: "₵", exchangeRate: 16.5 },
    embassy: { city: "Abuja", address: "Austrian Embassy Abuja (covers Ghana; documents can be verified at VFS Global Accra)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["WAEC verification", "University transcript attestation", "Police clearance from Ghana Police Service"], bankOptions: ["GCB Bank EUR transfer", "Ecobank Ghana international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Business", "Engineering", "IT", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Accra to Vienna",
  },
  {
    code: "ET",
    name: "Ethiopia",
    flag: "🇪🇹",
    demonym: "Ethiopian",
    currency: { code: "ETB", symbol: "Br", exchangeRate: 123 },
    embassy: { city: "Addis Ababa", address: "Austrian Embassy Addis Ababa", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Ministry of Education attestation", "Document Authentication Office verification", "Police clearance from Federal Police"], bankOptions: ["Commercial Bank of Ethiopia EUR transfer", "Dashen Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["Medicine", "Engineering", "IT", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path from Addis Ababa to Vienna",
  },
  {
    code: "TZ",
    name: "Tanzania",
    flag: "🇹🇿",
    demonym: "Tanzanian",
    currency: { code: "TZS", symbol: "TSh", exchangeRate: 2750 },
    embassy: { city: "Dar es Salaam", address: "Austrian Embassy (via Nairobi representation)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["NECTA verification", "TCU equivalency certificate", "Police clearance from Tanzania Police Force"], bankOptions: ["CRDB Bank EUR transfer", "NMB Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Business", "Engineering", "Environmental Science", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Dar es Salaam to Vienna",
  },
  {
    code: "ZA",
    name: "South Africa",
    flag: "🇿🇦",
    demonym: "South African",
    currency: { code: "ZAR", symbol: "R", exchangeRate: 20 },
    embassy: { city: "Pretoria", address: "Austrian Embassy Pretoria", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["SAQA evaluation", "Apostille from DIRCO", "Police clearance (SAPS)"], bankOptions: ["FNB EUR transfer", "Standard Bank international wire", "Nedbank forex services"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "Engineering", "Medicine", "Law"],
    commonQuestions: [],
    heroSubtitle: "Your path from Pretoria to Vienna",
  },
  {
    code: "CM",
    name: "Cameroon",
    flag: "🇨🇲",
    demonym: "Cameroonian",
    currency: { code: "XAF", symbol: "FCFA", exchangeRate: 655 },
    embassy: { city: "Yaoundé", address: "Austrian Embassy (via Abuja representation)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["Ministry of Higher Education attestation", "GCE/Baccalauréat verification", "Police clearance from Sûreté Nationale"], bankOptions: ["Afriland First Bank EUR transfer", "BICEC international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Engineering", "Medicine", "Business", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Yaoundé to Vienna",
  },
  {
    code: "UG",
    name: "Uganda",
    flag: "🇺🇬",
    demonym: "Ugandan",
    currency: { code: "UGX", symbol: "USh", exchangeRate: 4050 },
    embassy: { city: "Kampala", address: "Austrian Embassy (via Nairobi representation)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["UNEB verification", "NCHE equivalency letter", "Police clearance from Uganda Police Force"], bankOptions: ["Stanbic Bank EUR transfer", "Centenary Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 14 },
    popularPrograms: ["Medicine", "Engineering", "Business", "IT"],
    commonQuestions: [],
    heroSubtitle: "Your path from Kampala to Vienna",
  },
  {
    code: "RW",
    name: "Rwanda",
    flag: "🇷🇼",
    demonym: "Rwandan",
    currency: { code: "RWF", symbol: "FRw", exchangeRate: 1390 },
    embassy: { city: "Kigali", address: "Austrian Embassy (via Nairobi representation)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["REB certificate verification", "HEC equivalency letter", "Police clearance from Rwanda National Police"], bankOptions: ["Bank of Kigali EUR transfer", "I&M Bank Rwanda international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["IT", "Business", "Engineering", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Kigali to Vienna",
  },
  // ============================================================
  // LATIN AMERICA
  // ============================================================
  {
    code: "MX",
    name: "Mexico",
    flag: "🇲🇽",
    demonym: "Mexican",
    currency: { code: "MXN", symbol: "$", exchangeRate: 18.5 },
    embassy: { city: "Mexico City", address: "Sierra Tarahumara 420, Mexico City", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["SEP apostille", "Sworn translation to German", "Police clearance (Carta de Antecedentes No Penales)"], bankOptions: ["BBVA Mexico EUR transfer", "Banorte international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Architecture", "Business", "Engineering", "Arts"],
    commonQuestions: [],
    heroSubtitle: "Your path from Mexico City to Vienna",
  },
  {
    code: "AR",
    name: "Argentina",
    flag: "🇦🇷",
    demonym: "Argentine",
    currency: { code: "ARS", symbol: "$", exchangeRate: 1050 },
    embassy: { city: "Buenos Aires", address: "French 3671, Buenos Aires", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Apostille from Cancillería Argentina", "Sworn translation (traducción pública)", "Police clearance (Certificado de Antecedentes Penales)"], bankOptions: ["Banco Nación EUR transfer", "Banco Galicia international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Music", "Architecture", "Business", "Engineering"],
    commonQuestions: [],
    heroSubtitle: "Your path from Buenos Aires to Vienna",
  },
  {
    code: "CL",
    name: "Chile",
    flag: "🇨🇱",
    demonym: "Chilean",
    currency: { code: "CLP", symbol: "$", exchangeRate: 1010 },
    embassy: { city: "Santiago", address: "Austrian Embassy Santiago", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Apostille from Ministerio de Relaciones Exteriores", "Sworn translation to German", "Police clearance (Certificado de Antecedentes)"], bankOptions: ["Banco de Chile EUR transfer", "BCI international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Business", "Architecture", "Arts"],
    commonQuestions: [],
    heroSubtitle: "Your path from Santiago to Vienna",
  },
  {
    code: "PE",
    name: "Peru",
    flag: "🇵🇪",
    demonym: "Peruvian",
    currency: { code: "PEN", symbol: "S/", exchangeRate: 4.05 },
    embassy: { city: "Lima", address: "Austrian Embassy Lima", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Apostille from Cancillería", "Sworn translation to German", "Police clearance (Certificado de Antecedentes Policiales)"], bankOptions: ["BCP EUR transfer", "Interbank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Engineering", "Business", "Architecture", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Lima to Vienna",
  },
  {
    code: "EC",
    name: "Ecuador",
    flag: "🇪🇨",
    demonym: "Ecuadorian",
    currency: { code: "USD", symbol: "$", exchangeRate: 1.09 },
    embassy: { city: "Quito", address: "Austrian Embassy (via Lima representation)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Apostille from Cancillería", "Sworn translation to German", "Police clearance (Record Policial)"], bankOptions: ["Banco Pichincha EUR transfer", "Produbanco international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Business", "Medicine", "Environmental Science"],
    commonQuestions: [],
    heroSubtitle: "Your path from Quito to Vienna",
  },
  {
    code: "VE",
    name: "Venezuela",
    flag: "🇻🇪",
    demonym: "Venezuelan",
    currency: { code: "VES", symbol: "Bs.", exchangeRate: 40 },
    embassy: { city: "Caracas", address: "Austrian Embassy (via Bogotá representation)", appointmentWaitWeeks: "~4 weeks" },
    documents: {
      specific: [
        "Apostille from Cancillería (where obtainable)",
        "Sworn translation to German",
        "Police clearance (Certificado de Antecedentes Penales)",
      ],
      bankOptions: [
        "Alternative transfer methods may be required",
        "Wise/Remessa international transfer",
        "Ask your bank which transfer routes to the EU are allowed for you",
      ],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 12 },
    popularPrograms: ["Engineering", "Business", "Medicine", "IT"],
    commonQuestions: [
      { q: "What if I can't get documents from Venezuelan authorities?", a: "Some Austrian embassies accept statutory declarations or alternative documentation when Venezuelan government offices are inaccessible. Verify directly with the Austrian Embassy handling your application." },
      { q: "Can I apply from outside Venezuela?", a: "Yes. Many Venezuelan students apply through the Austrian Embassy in Bogotá, Colombia or other nearby representations." },
    ],
    heroSubtitle: "Your path to Vienna",
  },
  {
    code: "UY",
    name: "Uruguay",
    flag: "🇺🇾",
    demonym: "Uruguayan",
    currency: { code: "UYU", symbol: "$U", exchangeRate: 44 },
    embassy: { city: "Montevideo", address: "Austrian Embassy (via Buenos Aires representation)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Apostille from Ministerio de Relaciones Exteriores", "Sworn translation to German", "Police clearance (Certificado de Buena Conducta)"], bankOptions: ["BROU EUR transfer", "Itaú Uruguay international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 8 },
    popularPrograms: ["Business", "Engineering", "Architecture", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path from Montevideo to Vienna",
  },
  {
    code: "PY",
    name: "Paraguay",
    flag: "🇵🇾",
    demonym: "Paraguayan",
    currency: { code: "PYG", symbol: "₲", exchangeRate: 7950 },
    embassy: { city: "Asunción", address: "Austrian Embassy (via Buenos Aires representation)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Apostille from Ministerio de Relaciones Exteriores", "Sworn translation to German", "Police clearance (Certificado de Antecedentes Policiales)"], bankOptions: ["Banco Continental EUR transfer", "Itaú Paraguay international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Business", "Medicine", "Agriculture"],
    commonQuestions: [],
    heroSubtitle: "Your path from Asunción to Vienna",
  },
  {
    code: "BO",
    name: "Bolivia",
    flag: "🇧🇴",
    demonym: "Bolivian",
    currency: { code: "BOB", symbol: "Bs.", exchangeRate: 7.5 },
    embassy: { city: "La Paz", address: "Austrian Embassy (via Lima representation)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Apostille from Cancillería", "Sworn translation to German", "Police clearance (Certificado de Antecedentes Penales)"], bankOptions: ["Banco Mercantil Santa Cruz EUR transfer", "BNB international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 10 },
    popularPrograms: ["Engineering", "Medicine", "Business", "Environmental Science"],
    commonQuestions: [],
    heroSubtitle: "Your path from La Paz to Vienna",
  },
  // ============================================================
  // EASTERN EUROPE & ADDITIONAL
  // ============================================================
  {
    code: "BY",
    name: "Belarus",
    flag: "🇧🇾",
    demonym: "Belarusian",
    currency: { code: "BYN", symbol: "Br", exchangeRate: 3.56 },
    embassy: { city: "Minsk", address: "Austrian Embassy (via Moscow representation)", appointmentWaitWeeks: "~4 weeks" },
    documents: { specific: ["Ministry of Education apostille", "Notarized German translation", "Police clearance from Ministry of Interior"], bankOptions: ["Alternative transfer methods may be required", "Ask your bank which transfer routes to the EU are allowed for you"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 12 },
    popularPrograms: ["IT", "Engineering", "Medicine", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path to Vienna",
  },
  {
    code: "AF",
    name: "Afghanistan",
    flag: "🇦🇫",
    demonym: "Afghan",
    currency: { code: "AFN", symbol: "؋", exchangeRate: 76 },
    embassy: { city: "Islamabad", address: "Austrian Embassy Islamabad (covers Afghanistan)", appointmentWaitWeeks: "~6 weeks" },
    documents: { specific: ["Ministry of Education attestation", "Notarized German translation", "Police clearance (where available)"], bankOptions: ["Alternative transfer methods required", "Ask your bank which transfer routes to the EU are allowed for you"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 10, maxWeeks: 16 },
    popularPrograms: ["Medicine", "Engineering", "IT", "Business"],
    commonQuestions: [],
    heroSubtitle: "Your path to Vienna",
  },
  {
    code: "IL",
    name: "Israel",
    flag: "🇮🇱",
    demonym: "Israeli",
    currency: { code: "ILS", symbol: "₪", exchangeRate: 3.95 },
    embassy: { city: "Tel Aviv", address: "Austrian Embassy Tel Aviv", appointmentWaitWeeks: "~2 weeks" },
    documents: { specific: ["Apostille from Ministry of Foreign Affairs", "Notarized German translation", "Police clearance from Israel Police"], bankOptions: ["Bank Leumi EUR transfer", "Bank Hapoalim international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 4, maxWeeks: 6 },
    popularPrograms: ["Engineering", "Computer Science", "Business", "Arts"],
    commonQuestions: [],
    heroSubtitle: "Your path from Tel Aviv to Vienna",
  },
  {
    code: "KH",
    name: "Cambodia",
    flag: "🇰🇭",
    demonym: "Cambodian",
    currency: { code: "KHR", symbol: "៛", exchangeRate: 4430 },
    embassy: { city: "Bangkok", address: "Austrian Embassy Bangkok (covers Cambodia)", appointmentWaitWeeks: "~3 weeks" },
    documents: { specific: ["Ministry of Education attestation", "Notarized German translation", "Police clearance certificate"], bankOptions: ["ACLEDA Bank EUR transfer", "Canadia Bank international wire"] },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 6, maxWeeks: 12 },
    popularPrograms: ["Business", "IT", "Engineering", "Agriculture"],
    commonQuestions: [],
    heroSubtitle: "Your path from Phnom Penh to Vienna",
  },
];

// European destination comparison data for Cost Engine
export interface DestinationCost {
  country: string;
  flag: string;
  monthlyTotalEUR: number;
  breakdown: {
    tuition: number;
    rent: number;
    food: number;
    insurance: number;
    transport: number;
  };
}

export const destinations: DestinationCost[] = [
  { country: "Austria", flag: "🇦🇹", monthlyTotalEUR: 950, breakdown: { tuition: 60, rent: 450, food: 250, insurance: 60, transport: 130 } },
  { country: "Germany", flag: "🇩🇪", monthlyTotalEUR: 1020, breakdown: { tuition: 25, rent: 500, food: 260, insurance: 110, transport: 125 } },
  { country: "Netherlands", flag: "🇳🇱", monthlyTotalEUR: 1380, breakdown: { tuition: 180, rent: 650, food: 280, insurance: 120, transport: 150 } },
  { country: "France", flag: "🇫🇷", monthlyTotalEUR: 1150, breakdown: { tuition: 30, rent: 600, food: 300, insurance: 50, transport: 170 } },
  { country: "Sweden", flag: "🇸🇪", monthlyTotalEUR: 1250, breakdown: { tuition: 0, rent: 650, food: 320, insurance: 40, transport: 240 } },
];

export function getCountryByCode(code: string): CountryData | undefined {
  return countries.find((c) => c.code === code);
}

export function getGenericFallback(countryName: string): CountryData {
  return {
    code: "XX",
    name: countryName || "your country",
    flag: "🌍",
    demonym: "",
    currency: { code: "EUR", symbol: "€", exchangeRate: 1 },
    embassy: {
      city: "your nearest Austrian embassy",
      address: "Check bmeia.gv.at for your local embassy",
      appointmentWaitWeeks: "~3-5 weeks",
    },
    documents: {
      specific: [
        "Document attestation + apostille (varies by country)",
        "Certified German translation of all documents",
        "Police clearance from local authority",
      ],
      bankOptions: ["Check with your local bank for EUR transfer options"],
    },
    financialProof: { monthlyEUR: 533.85, blockedAccountEUR: 6406 },
    processingTime: { minWeeks: 8, maxWeeks: 12 },
    popularPrograms: ["Business", "Engineering", "Computer Science", "Medicine"],
    commonQuestions: [],
    heroSubtitle: "Your path to Vienna",
  };
}